import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
} from '@mui/material';
import { designerApi } from '@/utils/api';
import {
  getDesignerDraftState,
  saveDesignerConfiguration,
} from '@/designer/services/saveDesignerConfiguration';

const SEMVER_RE = /^\d+\.\d+\.\d+$/;

export function nextPatchVersion(v: string | null): string {
  if (!v || !SEMVER_RE.test(v)) {
    return '1.0.0';
  }
  const [major, minor, patch] = v.split('.').map((part) => Number(part));
  return `${major}.${minor}.${patch + 1}`;
}

function errorDetail(err: unknown): string {
  const e = err as { message?: string; detail?: string; response?: { data?: { detail?: string } } };
  return (
    e?.response?.data?.detail ||
    e?.detail ||
    e?.message ||
    'Publish failed'
  );
}

export interface PublishModuleDialogProps {
  open: boolean;
  moduleKey: string;
  connectionId: string;
  currentVersion: string | null;
  onClose(): void;
  onPublished(version: string, meta?: { stamped: boolean }): void;
}

export const PublishModuleDialog: React.FC<PublishModuleDialogProps> = ({
  open,
  moduleKey,
  connectionId,
  currentVersion,
  onClose,
  onPublished,
}) => {
  const [version, setVersion] = useState(() => nextPatchVersion(currentVersion));
  const [token, setToken] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setVersion(nextPatchVersion(currentVersion));
    setToken('');
    setDisplayName('');
    setDescription('');
    setInlineError(null);
    setSubmitting(false);
  }, [open, currentVersion, moduleKey]);

  const canPublish = SEMVER_RE.test(version.trim()) && token.trim().length > 0 && !submitting;

  const handlePublish = async () => {
    if (!canPublish) {
      return;
    }
    setInlineError(null);
    const draft = getDesignerDraftState();
    if (draft.hydrated && draft.dirty) {
      const saveRes = await saveDesignerConfiguration(connectionId);
      if (!saveRes.ok) {
        setInlineError(saveRes.error);
        return;
      }
      if (getDesignerDraftState().dirty) {
        setInlineError('Save the configuration first.');
        return;
      }
    }
    setSubmitting(true);
    const trimmedVersion = version.trim();
    try {
      const body: {
        connection_id: string;
        version: string;
        token: string;
        display_name?: string;
        description?: string;
      } = {
        connection_id: connectionId,
        version: trimmedVersion,
        token: token.trim(),
      };
      if (displayName.trim()) {
        body.display_name = displayName.trim();
      }
      if (description.trim()) {
        body.description = description.trim();
      }
      const res = await designerApi.publishModule(moduleKey, body);
      if (res.stamped !== false) {
        setSuccessToast(`Published ${moduleKey}@${trimmedVersion}`);
      }
      onPublished(trimmedVersion, { stamped: res.stamped !== false });
    } catch (err: unknown) {
      setInlineError(errorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Publish module</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="info" sx={{ mt: 1 }}>
            Publishing <strong>{moduleKey}</strong> from this connection to the module registry.
            Publishes the last saved configuration. Save in Designer first if you have unsaved edits.
          </Alert>
          <TextField
            label="Version"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            fullWidth
            required
            helperText="Semantic version (major.minor.patch)"
            autoFocus
          />
          <TextField
            label="Token"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            fullWidth
            required
            autoComplete="off"
            helperText="Registry publish token (entered per publish)"
          />
          <TextField
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            helperText="Optional — defaults to previous package or module key"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            helperText="Optional"
          />
          {inlineError ? (
            <Alert severity="error">{inlineError}</Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handlePublish()}
            disabled={!canPublish}
          >
            {submitting ? 'Publishing…' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successToast}
        autoHideDuration={5000}
        onClose={() => setSuccessToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {successToast ? (
          <Alert
            onClose={() => setSuccessToast(null)}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {successToast}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};
